import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ClientTheme } from '../types/theme.types';
import { themeRegistry } from '../utils/themeRegistry';
import { applyThemeVariables } from '../utils/themeHelpers';
import { getRuntimeEnv } from '../utils/runtimeEnv';

interface ThemeContextValue {
  clientTheme: ClientTheme | null;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  clientTheme: null,
  isLoading: false,
});

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Provider que gestiona el tema del cliente y aplica variables CSS
 * Los colores ya fueron actualizados en initTheme.ts antes del render
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [clientTheme, setClientTheme] = useState<ClientTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const clientName = getRuntimeEnv('REACT_APP_CLIENT_NAME');
    
    if (!clientName) {
      setIsLoading(false);
      return;
    }

    // Obtener tema (ya fue cargado en initTheme.ts)
    const loadedTheme = themeRegistry.getTheme(clientName);
    
    if (loadedTheme) {
      setClientTheme(loadedTheme);
      // Solo aplicar variables CSS (los colores ya fueron actualizados)
      applyThemeVariables(loadedTheme);
    }
    
    setIsLoading(false);
  }, []);

  return (
    <ThemeContext.Provider value={{ clientTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook para acceder al perfil de diseño del cliente
 */
export const useClientTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useClientTheme debe usarse dentro de ThemeProvider');
  }
  
  return context;
};





